import React, { useState, useMemo } from "react";
import { Search, X } from "lucide-react";

/**
 * Searchable multi-select of interest categories (macro level, same data model as before).
 * Typing matches a macro label OR any of its sub-category labels.
 * Props: macros [{id,label,tags}], value (array of tag ids), onChange(nextIds), prefix (testid prefix)
 */
export default function InterestsPicker({ macros = [], value = [], onChange, prefix = "interest" }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();

  const isOn = (m) => {
    const ids = (m.tags || []).map((t) => t.id);
    return ids.length > 0 && ids.every((id) => value.includes(id));
  };
  const toggle = (m) => {
    const ids = (m.tags || []).map((t) => t.id);
    const on = ids.length > 0 && ids.every((id) => value.includes(id));
    onChange(on ? value.filter((id) => !ids.includes(id)) : [...new Set([...value, ...ids])]);
  };

  const filtered = useMemo(() => {
    if (!query) return macros;
    return macros.filter(
      (m) =>
        (m.label || "").toLowerCase().includes(query) ||
        (m.tags || []).some((t) => (t.label || "").toLowerCase().includes(query))
    );
  }, [query, macros]);
  const selected = macros.filter(isOn);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca un interesse…"
          data-testid={`${prefix}-search`}
          className="w-full h-10 rounded-xl border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946]/30"
        />
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((m) => (
            <span key={m.id} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-[#E63946] text-white text-xs font-semibold">
              {m.label}
              <button type="button" onClick={() => toggle(m)} data-testid={`${prefix}-remove-${m.id}`} aria-label={`Rimuovi ${m.label}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto">
        {filtered.map((m) => {
          const on = isOn(m);
          return (
            <button
              type="button"
              key={m.id}
              data-testid={`${prefix}-${m.id}`}
              onClick={() => toggle(m)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${on ? "bg-[#E63946] text-white border-[#E63946]" : "bg-white text-slate-600 border-slate-200"}`}
            >
              {m.label}
            </button>
          );
        })}
        {filtered.length === 0 && <p className="text-sm text-slate-400 py-2">Nessun interesse trovato.</p>}
      </div>
    </div>
  );
}
